import Button from "../Button";
import Listbox from "../Listbox";
import { Badge } from "./subcomponents/Badges";

import { EnvData } from "../../src/types";
import { format } from "date-fns";
import { useState } from "react";
import FadeIn from "react-fade-in";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";

import { ExplainDetails } from "./subcomponents/ExplainDetails";
import { StarterPolicy } from "./types";
import {
  EXAMPLE_ACTOR_ID,
  EXAMPLE_RESOURCE_ID,
  EXAMPLE_ACTION,
} from "./constants";
import {
  useAuthorization,
  LogItem,
  createEmptyLogRows,
} from "./EnforcePage.utils";

const ACTIONS = ["choose an action", "view", "edit"] as const;
type ActionType = typeof ACTIONS[number];
const ACTIONS_ARRAY: ActionType[] = [...ACTIONS];

interface EnforcePageProps {
  selectedEnv: EnvData;
  policy: StarterPolicy;
  startTime: string;
  onBack: () => void;
  onComplete: () => void;
  logAnalytics?: (message: string) => void;
}

export const EnforcePage: React.FC<EnforcePageProps> = ({
  selectedEnv,
  policy,
  startTime,
  onBack,
  onComplete,
  logAnalytics,
}) => {
  // Authorization form state
  const [actorId, setActorId] = useState<string>("");
  const [resourceId, setResourceId] = useState<string>("");
  const [action, setAction] = useState<ActionType>("choose an action");

  const [queryDetails, setQueryDetails] = useState<LogItem | null>(null);

  const { log, authorize, madeAuthorizationRequest } = useAuthorization(
    selectedEnv,
    startTime
  );

  return (
    <div className="mx-auto max-w-[800px]">
      <FadeIn>
        <h2 className="text-2xl">Making an authorization decision</h2>
        <div className="mt-6" />
        <div>
          An authorization decision takes the form of:{" "}
          <span>
            Can <Badge>{policy.actor}</Badge> perform an <Badge>action</Badge>{" "}
            on <Badge>{policy.businessObject}</Badge>?
          </span>
        </div>
        <div className="mt-2" />
        <div>
          Check that the data you added in the last step grants{" "}
          <Badge>
            {policy.actor}:{EXAMPLE_ACTOR_ID}
          </Badge>{" "}
          the <Badge>{EXAMPLE_ACTION}</Badge> permission for{" "}
          <Badge>
            {policy.businessObject}:{EXAMPLE_RESOURCE_ID}
          </Badge>
          .
        </div>
        <div className="mt-8 mb-9 grid grid-cols-2 gap-8">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (logAnalytics) logAnalytics("Check Access");
              authorize({
                actor_type: policy.actor,
                actor_id: actorId,
                action: action,
                resource_type: policy.businessObject,
                resource_id: resourceId,
              });
            }}
          >
            <div className="grid grid-cols-2 gap-2 items-center">
              <div>
                <label htmlFor="userId">{policy.actor}</label>
              </div>
              <div>
                <input
                  className={`border-2 border-get-started-actor px-2 py-1 text-base rounded w-full`}
                  placeholder={EXAMPLE_ACTOR_ID}
                  id="actorId"
                  value={actorId}
                  onChange={(e) => {
                    setActorId(e.target.value);
                  }}
                />
              </div>
              <div>
                <label htmlFor="action">Action</label>
              </div>
              <div>
                <Listbox
                  options={ACTIONS_ARRAY}
                  onChange={(a) => {
                    // Guaranteed not to be an array but satisfying static type checker
                    if (!Array.isArray(a)) {
                      setAction(a as ActionType);
                    }
                  }}
                  selectedValue={action}
                  className="rounded border border-2 border-get-started-action"
                  hoverClassName="text-black bg-get-started-resource"
                  activeClassName="text-get-started-action"
                />
              </div>
              <div>
                <label htmlFor="resourceId">{policy.businessObject}</label>
              </div>
              <div>
                <input
                  className="border border-2 border-get-started-resource px-2 py-1 text-base rounded w-full"
                  placeholder={EXAMPLE_RESOURCE_ID}
                  id="resourceId"
                  value={resourceId}
                  onChange={(e) => {
                    setResourceId(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="mt-4" />
            <Button
              medium
              theme="primary"
              disabled={
                actorId.length === 0 ||
                resourceId.length === 0 ||
                action === "choose an action"
              }
            >
              Check access
            </Button>
          </form>
          <div>
            <div className="bg-gray-300 px-2 py-1 rounded-t">
              Preview payload
            </div>
            <div className="relative text-sm text-[#d6d6d6] px-2 py-1 bg-[#2e2e2e]">
              <pre className="whitespace-pre-wrap">
                {`{\n`}
                {`  "actor_type": "${policy.actor}",\n`}
                {`  "actor_id": `}
                <span className="text-get-started-actor font-semibold">
                  {`"${actorId}"`}
                </span>
                {`,\n`}
                {`  "action": `}
                <span className="text-get-started-action font-semibold">{`"${action}"`}</span>
                {`,\n`}
                {`  "resource_type": "${policy.businessObject}",\n`}
                {`  "resource_id": `}
                <span className="text-get-started-resource font-semibold">
                  {`"${resourceId}"`}
                </span>
                {`\n`}
                {`}`}
              </pre>
            </div>
          </div>
        </div>
        <div className="mt-4" />
        <div className="text-lg font-semibold">Recent authorizations</div>
        <div className="mt-2 mb-8">
          <table className="text-sm table-auto w-full">
            <thead>
              <tr className="border-b text-sm text-gray-600">
                <td className="p-2">Time</td>
                <td className="p-2 text-center">Result</td>
                <td className="p-2 text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <div>Connected</div>
                    <div>
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-600 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600" />
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              {log.map((logItem) => {
                return (
                  <tr
                    key={logItem.requestId}
                    className="bg-white odd:bg-gray-100"
                  >
                    <td className="p-2">
                      <FadeIn>
                        {format(
                          new Date(logItem.timestamp),
                          "MMMM d, h:mm:ss aaa"
                        )}
                      </FadeIn>
                    </td>
                    <td className="p-2 text-center">
                      <FadeIn>
                        {logItem.result === "allowed" ? (
                          <span className="bg-green-700 text-white px-2 py-1 rounded font-bold text-sm uppercase">
                            Allowed
                          </span>
                        ) : (
                          <span className="bg-red-700 text-white px-2 py-1 rounded font-bold text-sm uppercase">
                            Denied
                          </span>
                        )}
                      </FadeIn>
                    </td>
                    <td className="p-2 text-right">
                      <FadeIn>
                        <a
                          href="#"
                          className="underline"
                          onClick={(e) => {
                            e.preventDefault();
                            if (logAnalytics) logAnalytics("Explain");
                            setQueryDetails(logItem);
                          }}
                        >
                          Show me why
                        </a>
                      </FadeIn>
                    </td>
                  </tr>
                );
              })}
              {log.length === 0 && (
                <>
                  <tr className="bg-white odd:bg-gray-100">
                    <td colSpan={3} className="p-2">
                      Waiting for your first authorization check
                    </td>
                  </tr>
                </>
              )}
              {createEmptyLogRows(log.length).map((idx) => {
                return (
                  <tr key={idx} className="bg-white odd:bg-gray-100">
                    <td colSpan={3} className="h-8"></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4" />
        <div className="flex flex-row justify-between mt-4">
          <Button icon={<AiOutlineArrowLeft />} medium onClick={onBack}>
            Review data
          </Button>
          {madeAuthorizationRequest && (
            <FadeIn>
              <Button
                theme="primary"
                icon={<AiOutlineArrowRight />}
                medium
                onClick={onComplete}
              >
                Wrap it up
              </Button>
            </FadeIn>
          )}
        </div>
      </FadeIn>
      <ExplainDetails
        selectedEnv={selectedEnv}
        log={queryDetails!}
        isOpen={queryDetails !== null}
        onClose={() => setQueryDetails(null)}
      />
    </div>
  );
};
